import React, { useState, useEffect, useContext, useRef } from "react";
import toast from "react-hot-toast";
import { generateRandomString } from "js/helpers/header";
import Switch from "@mui/material/Switch";
import lockedIcon from "images/Locked_icon.png";
import unlockedIcon from "images/Unlocked_icon.png";
import DoneRoundedIcon from "@mui/icons-material/DoneRounded";
import LockRoundedIcon from "@mui/icons-material/LockRounded";
import LockOpenRoundedIcon from "@mui/icons-material/LockOpenRounded";

import {
    fetchDeviceSettings,
    fetchPolicyGroupSettings,
    updateDeviceSetting,
    updatePolicyGroupSetting,
    updatePolicySettingLock
} from "js/Api/index";
import { Button } from '../../../Component/CommonFormEle';

function PairingTab({ policy, settingsFrom }) {
    const [settings, setSettings] = useState([]);
    const [showQRCodeAndInviteButton, setShowQRCodeAndInviteButton] = useState(false);
    const [showUnpairButton, setShowUnpairButton] = useState(false);
    const [policySettingCategoryGroups, setPolicySettingCategoryGroups] = useState([]);
    const [selectedPairingOption, setSelectedPairingOption] = useState("allow");

    // 添加状态来跟踪变化的设置
    const [changedSettings, setChangedSettings] = useState(new Set());
    // 添加状态来存储初始值
    const [initialValues, setInitialValues] = useState({
        showQRCodeAndInviteButton: false,
        showUnpairButton: false
    });

    const isReadOnly = selectedPairingOption !== "manage";

    useEffect(() => {
        fetchPairingSettings();
    }, []);

    const fetchPairingSettings = async () => {
        try {
            const response = await fetchPolicyGroupSettings(policy.id);

            if (response.success) {
                setSettings(response.data.polictSettings);
                setPolicySettingCategoryGroups(response.data.policySettingCategoryGroups);
                updatePairingState(response.data.polictSettings);
                updateSelectedPairingOption(response.data.policySettingCategoryGroups);
                saveInitialValues(response.data.polictSettings);
            } else {
                console.error("Failed to fetch Pairing settings:", response.error);
            }
        } catch (error) {
            console.error("Error fetching Pairing settings:", error);
        }
    };


    const saveInitialValues = (pairingSettings) => {
        const values = {
            showQRCodeAndInviteButton: false,
            showUnpairButton: false
        };

        pairingSettings.forEach((setting) => {
            switch (setting.name) {
                case "Show QR Code and Invite by Email button on device":
                    values.showQRCodeAndInviteButton = setting.value === "true";
                    break;
                case "Show Unpair button on device":
                    values.showUnpairButton = setting.value === "true";
                    break;
                default:
                    break;
            }
        });

        setInitialValues(values);
    };

    const updatePairingState = (pairingSettings) => {
        pairingSettings.forEach((setting) => {
            switch (setting.name) {
                case "Show QR Code and Invite by Email button on device":
                    setShowQRCodeAndInviteButton(setting.value === "true");
                    break;
                case "Show Unpair button on device":
                    setShowUnpairButton(setting.value === "true");
                    break;
                default:
                    break;
            }
        });
    };

    const updateSelectedPairingOption = (categoryGroups) => {
        const pairingCategory = categoryGroups.find(
            (category) => category.categoryName === "Pairing"
        );
        if (pairingCategory) {
            setSelectedPairingOption(pairingCategory.locked ? "manage" : "allow");
        }
    };

    // 修改设置变更处理函数
    const handleQRCodeButtonChange = (event) => {
        const newValue = event.target.checked;
        setShowQRCodeAndInviteButton(newValue);

        if (newValue !== initialValues.showQRCodeAndInviteButton) {
            setChangedSettings(prev => new Set([...prev, "Show QR Code and Invite by Email button on device"]));
        } else {
            setChangedSettings(prev => {
                const newSet = new Set(prev);
                newSet.delete("Show QR Code and Invite by Email button on device");
                return newSet;
            });
        }
    };

    const handleUnpairButtonChange = (event) => {
        const newValue = event.target.checked;
        setShowUnpairButton(newValue);

        if (newValue !== initialValues.showUnpairButton) {
            setChangedSettings(prev => new Set([...prev, "Show Unpair button on device"]));
        } else {
            setChangedSettings(prev => {
                const newSet = new Set(prev);
                newSet.delete("Show Unpair button on device");
                return newSet;
            });
        }
    };

    const handleSavePairing = async () => {

        const updatedSettings = settings.filter((setting) => {
            if (!changedSettings.has(setting.name)) {
                return false;
            }

            switch (setting.name) {
                case "Show QR Code and Invite by Email button on device":
                    setting.value = showQRCodeAndInviteButton.toString();
                    return true;
                case "Show Unpair button on device":
                    setting.value = showUnpairButton.toString();
                    return true;
                default:
                    return false;
            }
        });

        try {
            if (updatedSettings.length === 0) {
                toast.info("No settings have been changed");
                return;
            }

            let hasError = false;

            for (const setting of updatedSettings) {
                const response = await updatePolicyGroupSetting(policy.id, setting.id, setting.value);

                if (!response.success) {
                    console.error(`Failed to update setting ${setting.name}:`, response.error);
                    hasError = true;
                }
            }

            if (hasError) {
                toast.error("Failed to update some Pairing settings");
            } else {
                toast.success("Pairing settings updated successfully!");
                setChangedSettings(new Set()); // Reset changes
                fetchPairingSettings(); // Refresh settings
            }
        } catch (error) {
            console.error("Error updating Pairing settings:", error);
            toast.error("Failed to update Pairing settings");
        }
    };

    const handleCategoryLockChange = async (locked) => {
        const category = policySettingCategoryGroups.find(
            (category) => category.categoryName === "Pairing"
        );

        if (!category) return;

        try {
            const response = await updatePolicySettingLock(policy.id, category.id, locked);

            if (response.success) {
                // Update local state
                category.locked = locked;
                setPolicySettingCategoryGroups([...policySettingCategoryGroups]);
                setSelectedPairingOption(locked ? "manage" : "allow");
            } else {
                console.error("Failed to update category lock:", response.error);
                toast.error("Failed to update category lock");
            }
        } catch (error) {
            console.error("Error updating category lock:", error);
            toast.error("Failed to update category lock");
        }
    };

    return (
        <>
            <div className="row">
                <div className="col-md-12">
                    <h4 className="d-inline">Pairing</h4>
                    <div className="dropdown d-inline">
                        <button className="btn lockerBtn" type="button" data-bs-toggle="dropdown" aria-expanded="false" >
                            {selectedPairingOption === "allow" ? (
                                <img src={unlockedIcon} style={{ height: "20px" }} />
                            ) : (
                                <img src={lockedIcon} style={{ height: "20px" }} />
                            )}
                        </button>
                        <ul className="dropdown-menu dropdown-item-ul">
                            <li onClick={() => handleCategoryLockChange(false)}>
                                <span>
                                    <LockOpenRoundedIcon sx={{ width: "20px", margin: "0 5px" }} />
                                </span>
                                Allow device user and family to change these settings
                                {selectedPairingOption === "allow" && (
                                    <span style={{ float: "right", marginRight: "1em" }}>
                                        <DoneRoundedIcon />
                                    </span>
                                )}
                            </li>
                            <li onClick={() => handleCategoryLockChange(true)}>
                                <span>
                                    <LockRoundedIcon
                                        sx={{
                                            width: "20px",
                                            margin: "0px 5px",
                                        }}
                                    />
                                </span>
                                Organisation manages these settings
                                {selectedPairingOption === "manage" && (
                                    <span style={{ float: "right", marginRight: "1em" }}>
                                        <DoneRoundedIcon />
                                    </span>
                                )}
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
            <div className="row mt-2">
                <div className="col-md-6">
                    Show QR code and Invite by Email button on device
                </div>
                <div className="col-md-6">
                    <Switch
                        checked={showQRCodeAndInviteButton}
                        onChange={handleQRCodeButtonChange}
                        color="primary"
                        disabled={isReadOnly}
                    />
                </div>
            </div>
            <div className="row">
                <div className="col-md-6">
                    Show Unpair button on device
                </div>
                <div className="col-md-6">
                    <Switch
                        checked={showUnpairButton}
                        onChange={handleUnpairButtonChange}
                        color="primary"
                        disabled={isReadOnly}
                    />
                </div>
            </div>
            <div className="row">
                <div className="col-md-12">
                    <Button onClick={handleSavePairing} variant="black">
                        Apply
                    </Button>
                </div>
            </div>
        </>
    );
}

export default PairingTab;